'use client';

import { DataTableViewOptions, FilterTagsRow, openColumnFilter, useFilterTags } from '@components/table';
import { useApi } from '@data-access';
import { FiltersHelper } from '@helpers/filters';
import { taskTypeLabelMap } from '@lib';
import { datasetInventoryCounterStore } from '@stores';
import { ColumnFilter, ColumnFiltersState, Table } from '@tanstack/react-table';
import isEqual from 'lodash.isequal';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useMemo } from 'react';

import styles from './DatasetInventoryTableToolbar.module.scss';

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  tableId: number;
  tableName: string;
  columnFilters: ColumnFilter[];
  defaultFilters: ColumnFilter[];
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>;
  setDefaultFilters: Dispatch<SetStateAction<ColumnFilter[]>>;
  viewOptionsContainer?: HTMLElement | null;
  hideFilterPersistence?: boolean;
  hideViewOptions?: boolean;
}

const FILTER_META: Record<string, { title: string; getLabel: (v: string) => string }> = {
  status: { title: 'Status', getLabel: v => v.toUpperCase() },
  taskType: { title: 'Task type', getLabel: v => taskTypeLabelMap[v] ?? v },
  aiModelName: { title: 'Model', getLabel: v => v },
};

export const DataTableToolbar = <TData,>({
  table,
  tableId,
  tableName,
  columnFilters,
  defaultFilters,
  setColumnFilters,
  setDefaultFilters,
  viewOptionsContainer,
  hideFilterPersistence = false,
  hideViewOptions = false,
}: DataTableToolbarProps<TData>) => {
  const isFiltered = table.getState().columnFilters?.length > 0;
  const { api } = useApi();
  const { query } = useRouter();

  const isDefault = useMemo(() => isEqual(columnFilters, defaultFilters), [columnFilters, defaultFilters]);

  const { setLoader } = datasetInventoryCounterStore;

  const { activeFilterGroups, removeFilterGroup, removeFilterValue } = useFilterTags({
    columnFilters,
    setColumnFilters,
    setLoader,
    filterMeta: FILTER_META,
    table,
  });

  const showPersistence =
    !hideFilterPersistence && Boolean((!defaultFilters && columnFilters?.length) || (defaultFilters && !isDefault));

  return (
    <div className={styles.toolbar}>
      {!hideViewOptions && (
        <DataTableViewOptions
          table={table}
          tableId={tableId}
          tableName={tableName}
          container={viewOptionsContainer}
        />
      )}
      <FilterTagsRow
        groups={activeFilterGroups}
        onRemoveValue={removeFilterValue}
        onRemoveGroup={removeFilterGroup}
        onTagClick={openColumnFilter}
        actions={{
          reset:
            isFiltered && isDefault
              ? {
                  label: 'Reset',
                  onClick: () => {
                    setLoader(true);
                    table.resetColumnFilters();
                  },
                }
              : undefined,
          backToDefault: showPersistence
            ? {
                label: 'Back to default',
                onClick: () => {
                  setLoader(true);
                  setColumnFilters(defaultFilters || []);
                },
              }
            : undefined,
          save: showPersistence
            ? {
                label: 'Save filters',
                onClick: () => FiltersHelper.saveFilters(api, query, setDefaultFilters, tableId),
              }
            : undefined,
        }}
      />
    </div>
  );
};
